import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useClaimService } from '../../services/claimService';
import { useCatalogService } from '../../services/catalogService';

interface ClaimEditModalProps {
  open: boolean;
  onClose: () => void;
  claim: {
    id?: number;
    externalClaimId?: string;
    dateOfServiceFrom?: string;
    deliveryMethod?: string;
    status?: string;
    readyForSync?: boolean; // ✅ NUEVO: Campo readyForSync
  } | null;
  onSuccess: () => void;
}

const ClaimEditModal: React.FC<ClaimEditModalProps> = ({
  open,
  onClose,
  claim,
  onSuccess
}) => {
  const { updateClaim } = useClaimService();
  const { getAllCatalogs } = useCatalogService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalogs, setCatalogs] = useState<any>({});
  const [formData, setFormData] = useState({
    dateOfServiceFrom: null as Date | null,
    deliveryMethod: ''
  });

  // Cargar catálogos al abrir el modal
  useEffect(() => {
    if (open) {
      loadCatalogs();
    }
  }, [open]);

  // Cargar datos del claim cuando se abre el modal
  useEffect(() => {
    if (open && claim) {
      setFormData({
        dateOfServiceFrom: claim.dateOfServiceFrom 
          ? new Date(claim.dateOfServiceFrom + (claim.dateOfServiceFrom.length === 10 ? 'T12:00:00' : ''))
          : null,
        deliveryMethod: claim.deliveryMethod || ''
      });
      setError(null);
    }
  }, [open, claim]);

  const loadCatalogs = async () => {
    try {
      const catalogsData = await getAllCatalogs();
      setCatalogs(catalogsData || {});
    } catch (error) {
      console.error('Error loading catalogs:', error);
    }
  };

  const getCatalogDescription = (catalogKey: string, value: string): string => {
    const catalog = catalogs[catalogKey];
    if (!catalog || !Array.isArray(catalog)) return value;
    const item = catalog.find((item: any) => item.value === value || item.code === value);
    return item?.description || item?.name || value;
  };

  const renderCatalogOptions = (catalogKey: string) => {
    const catalog = catalogs[catalogKey];
    if (!catalog || !Array.isArray(catalog)) return null;
    
    return catalog.map((item: any) => (
      <MenuItem key={item.value || item.code} value={item.value || item.code}>
        {item.description || item.name || item.value || item.code}
      </MenuItem>
    ));
  };

  const handleSubmit = async () => {
    if (!claim || !claim.id) {
      setError('No se puede actualizar el claim: falta el ID');
      return;
    }

    if (!formData.dateOfServiceFrom) {
      setError('La fecha de servicio es obligatoria');
      return;
    }

    if (!formData.deliveryMethod) {
      setError('El método de entrega es obligatorio');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Formatear fecha como YYYY-MM-DD
      const dateStr = formData.dateOfServiceFrom.toISOString().split('T')[0];
      
      const claimData = {
        dateOfServiceFrom: dateStr,
        deliveryMethod: formData.deliveryMethod
      };

      const response = await updateClaim(claim.id, claimData);

      if (response.success && response.data) {
        onSuccess();
        onClose();
      } else {
        setError(response.error || 'Error al actualizar el claim');
      }
    } catch (err: any) {
      console.error('Error updating claim:', err);
      setError(err.message || 'Error al actualizar el claim');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            Editar Cita
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {claim?.externalClaimId && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>ID ComPsych:</strong> {claim.externalClaimId}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Este claim ya está sincronizado con ComPsych. Los cambios pueden afectar la sincronización.
                </Typography>
              </Alert>
            )}

            <DatePicker
              label="Fecha de Servicio *"
              value={formData.dateOfServiceFrom}
              onChange={(date) => {
                setFormData(prev => ({ ...prev, dateOfServiceFrom: date }));
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !formData.dateOfServiceFrom
                }
              }}
            />

            <FormControl fullWidth required error={!formData.deliveryMethod}>
              <InputLabel>Método de Entrega *</InputLabel>
              <Select
                value={formData.deliveryMethod}
                onChange={(e) => setFormData(prev => ({ ...prev, deliveryMethod: e.target.value as string }))}
                label="Método de Entrega *"
              >
                {renderCatalogOptions('DELIVERY_METHOD')}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Envío Sync:
              </Typography>
              {claim?.readyForSync || claim?.externalClaimId ? (
                <Chip
                  label={
                    claim?.externalClaimId
                      ? 'Sincronizada'
                      : 'Enviado a Sync (irreversible)'
                  }
                  color={claim?.externalClaimId ? 'success' : 'warning'}
                  size="small"
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Pendiente. Usa «Marcar para Sync» en la pantalla de citas.
                </Typography>
              )}
            </Box>

            {claim?.status && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Estado actual:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {claim.status}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !formData.dateOfServiceFrom || !formData.deliveryMethod}
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ClaimEditModal;

